import { Divider, ETextStyleVariant, GeneralCaretLeftIcon, Text } from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import { Lightbox, LightboxMedia } from '@/components/common/Lightbox';
import Rating from '@/components/route/listing/Rating/Rating';
import Avatar from '@/components/switchback/Avatar';

import { IReview } from './PhotosFromTravelers';

type TReviewCardProps = {
  review: IReview;
};

const ReviewCard = ({ review }: TReviewCardProps) => {
  const intl = useIntl();

  const { isAboveTablet } = useBreakpointContext();

  const [expanded, setExpanded] = useState(false);

  const content = (
    <>
      <div className="relative flex p-4 pb-1 gap-4 md:px-6 md:py-5">
        {!isAboveTablet && (
          <GeneralCaretLeftIcon
            className={`absolute top-0.5 right-0.5 w-10 h-10 p-2 transition-all duration-300 ${
              expanded ? '-rotate-90' : 'rotate-90'
            }`}
          />
        )}

        <Avatar size="128" name={review.name} photoUrl={review.photoUrl} className="w-10 h-10" />

        <div className="flex flex-col">
          <Text variant={ETextStyleVariant.MediumBold}>{review.name}</Text>

          <div className="flex gap-2">
            <Text variant={ETextStyleVariant.SmallRegular}>
              {intl.formatDate(review.date, {
                month: 'long',
                year: 'numeric',
              })}
            </Text>

            {review.ratingNumber > 0 && (
              <Text variant={ETextStyleVariant.SmallBold}>
                <Rating number={review.ratingNumber} />
              </Text>
            )}
          </div>
        </div>
      </div>

      <Divider />

      <Text
        variant={ETextStyleVariant.SmallRegular}
        className={`px-4 text-gray-500 md:px-6 my-2 ${
          expanded || isAboveTablet ? 'overflow-auto' : 'line-clamp-2 h-10'
        }`}>
        {review.review}
      </Text>
    </>
  );

  if (isAboveTablet) {
    return (
      <div className="flex flex-col bg-white border border-gray-200 rounded lg:w-80 shrink-0 md:h-2/5 lg:h-full md:-mb-20 lg:mb-0">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`w-full overflow-hidden bg-white rounded-t transition-all duration-300 absolute bottom-0 left-0 right-0 shadow-[0_0_0.4rem_rgb(0_0_0_/_0.2)] -mb-32 ${
        expanded ? 'h-2/5' : 'h-36'
      }`}
      aria-expanded={expanded}
      onClick={() => setExpanded(!expanded)}>
      <div className="flex flex-col h-full overflow-hidden text-left">{content}</div>
    </button>
  );
};

type TReviewSlideProps = {
  image: string;
  index: number;
  review: IReview;
};

const ReviewSlide = ({ image, index, review }: TReviewSlideProps) => {
  const intl = useIntl();

  return (
    <div className="flex flex-col justify-center w-full h-full py-32">
      <div className="relative h-full md:gap-3 md:flex md:flex-col lg:flex-row md:px-4 lg:px-0">
        <figure className="flex items-center flex-grow h-full py-5 overflow-hidden bg-white md:bg-gray-850 md:rounded md:py-0">
          <LightboxMedia
            url={image}
            alt={intl.formatMessage(
              { defaultMessage: 'Review photo {photoPosition}', id: 'E6Wrwl' },
              { photoPosition: index + 1 },
            )}
          />
        </figure>

        <ReviewCard review={review} />
      </div>
    </div>
  );
};

type TPhotoPreviewModalProps = {
  reviews: IReview[];
  activeSlide?: number;
  onClose: () => void;
};

export const PhotoPreviewModal = ({
  reviews,
  activeSlide = 0,
  onClose,
}: TPhotoPreviewModalProps) => {
  return (
    <Lightbox initialSlide={activeSlide} onClose={onClose}>
      {reviews.flatMap(
        (review: IReview) =>
          review.images?.map((image, index) => (
            <ReviewSlide key={image} index={index} review={review} image={image} />
          )) ?? [],
      )}
    </Lightbox>
  );
};
